/* eslint-disable */
// @ts-nocheck
import React from 'react';
import * as R from 'ramda';
import classNames from 'classnames';
import {
  AreaSeries,
  DiscreteColorLegend,
  Hint,
  HorizontalGridLines,
  LineMarkSeries,
  makeWidthFlexible,
  XAxis,
  XYPlot,
  YAxis,
} from 'react-vis';
import * as d3Scale from 'd3-scale';
import { subscribe } from 'subscribe-ui-event';
import { applyTimezoneOffsetToTickValues, addFirstOrLastTick, handleSingleDayDomain } from '../../../main/support/dateUtils';
type TickValue = Record<string, any>;

/*
 * Determines max tick count to fit constraints
 * constraints: plot width, data points
 */
function findMaxTickCount(num: number, max: number) {
  var output = num;
  var divisor = 1;

  while (output > max) {
    output = num / divisor;
    divisor++;
  }

  output = Math.ceil(output);
  return output;
}

function getTickCount(arr: Array<TickValue>, width: number) {
  var length = arr.length;
  var maxTickCount;

  if (width <= 400) {
    maxTickCount = 3;
  } else if (width <= 750) {
    maxTickCount = 9;
  } else {
    maxTickCount = 15;
  }

  return findMaxTickCount(length, maxTickCount);
}

function removeNonMidnightTicks(tickValues: Array<TickValue> = []) {
  return tickValues.filter((tick) => {
    return tick.getHours() === 0;
  });
}

// shift tick 12 hours so it will be centered. convert to Date obj for react-vis.
function handleSingleDayTick(tickValues: Array<TickValue>) {
  return tickValues.map((tick) => {
    return new Date(tick.clone().add(12, 'hours'));
  });
}

// add 12 hours to each mark to center
function handleSingleDayLines(lines: Record<string, any>) {
  return R.mapObjIndexed((line) => {
    return R.assoc(
      'data',
      line.data.map((mark) => {
        return R.assoc('x', mark.x.clone().add(12, 'hours'), mark);
      }),
      line
    );
  }, lines);
}

function handleSinglePointDomain(lines: Record<string, any>, key: string) {
  const yVal = lines[key].data[0].y;
  return [0, yVal * 2];
}

const FlexibleXYPlot = makeWidthFlexible(XYPlot);

/**
 * Linear scale name.
 * @type {string}
 * @const
 */
const LINEAR_SCALE_TYPE = 'linear';

/**
 * Ordinal scale name.
 * @type {string}
 * @const
 */
const ORDINAL_SCALE_TYPE = 'ordinal';

/**
 * Category scale.
 * @type {string}
 * @const
 */
const CATEGORY_SCALE_TYPE = 'category';

/**
 * Log scale name.
 * @type {string}
 * @const
 */
const LOG_SCALE_TYPE = 'log';

/**
 * Time scale name.
 * @type {string}
 * @const
 */
const TIME_SCALE_TYPE = 'time';

/**
 * Time UTC scale name.
 * @type {string}
 * @const
 */
const TIME_UTC_SCALE_TYPE = 'time-utc';

/**
 * Line colors
 * @type {Array}
 * @const
 */
const PREDEFINED_COLORS = [
  '#0075c9', // new - primary main (@java)
  '#8b41af', // new - illustration deep purple
  '#28b37f', // old (deprecated) - @jungle-green
  '#ffba43', // old (deprecated) - @yellow-orange
  '#8dbee7', // old (deprecated) - @sky
  '#06122f', // old (deprecated) - @black-pearl
  '#7ab800', // old (deprecated) - @lime
  '#ff0078', // old (deprecated) - @deep-pink
];

/**
 * Scale functions that are supported in the library.
 * @type {Object}
 * @const
 */
const SCALE_FUNCTIONS = {
  [LINEAR_SCALE_TYPE]: d3Scale.scaleLinear,
  [ORDINAL_SCALE_TYPE]: d3Scale.scalePoint,
  [CATEGORY_SCALE_TYPE]: d3Scale.scaleOrdinal,
  [LOG_SCALE_TYPE]: d3Scale.scaleLog,
  [TIME_SCALE_TYPE]: d3Scale.scaleTime,
  [TIME_UTC_SCALE_TYPE]: d3Scale.scaleUtc,
};
const DEFAULT_PROPS = {
  colors: PREDEFINED_COLORS,
  height: 300,
  margin: {
    left: 50,
    right: 25,
    top: 10,
    bottom: 40,
  },
  legendWidth: 150,
  showHint: true,
  showLegend: true,
  xAxis: {
    title: '',
    tickSize: 3,
  },
  xType: 'linear',
  yAxis: {
    title: '',
    tickSize: 3,
  },
  yType: 'linear',
};
type State = {
  /**
   * title of series that is currently clicked
   */
  active: string | null | undefined;

  /**
   * title of series that is currently hovered
   */
  focus: string | null | undefined;

  /**
   * dynamic width of the line chart
   */
  plotWidth: number;

  /**
   * Represents the value of the currently hovered point.
   */
  value:
    | {
        key: string;
        x: string | number;
        y: string | number;
      }
    | null
    | undefined;
  activeChartIndex: number;
};
type AxisType = {
  title: string;
  tickFormat: (arg0: number | string) => number | string;
  hintFormat: (arg0: number | string) => number | string;
  tickValues: Array<Record<string, any>>;
};
type Props = {
  /**
   * Colors for lines to be shown
   */
  colors?: Array<string>;

  /**
   * Height of the chart
   */
  height?: number;

  /**
   * whether or not to include a dashed component of the line chart
   */
  includeDashedLine: boolean;

  /**
   * If includeDashedLine is true, this function will iterate over each point and return true for points that should be included as part of the dashed line
   */
  includePointInDashedPart: (arg0: Record<string, any>) => boolean;

  /**
   * this function will iterate over each point and return true for points that should be included as part of the solid (default) line
   */
  includePointInSolidPart: (arg0: Record<string, any>) => boolean;

  /**
   * Legend width
   */
  legendWidth?: number;

  /**
   * Data for rendering line series
   */
  lines: Record<string, any>;

  /**
   * Margin around the chart.
   */
  margin?: Record<string, any>;

  /**
   * Show hint component
   */
  showHint: boolean;

  /**
   * Show legend
   */
  showLegend: boolean;

  /**
   * Title to display above the line chart
   */
  title: string;

  /**
   * Width of the chart
   */
  width: number;

  /**
   * Config for X Axis
   */
  xAxis: AxisType;

  /**
   * X Axis type
   */
  xType?: string;

  /**
   * Config for Y Axis
   */
  yAxis: AxisType;

  /**
   * Y Axis type
   */
  yType?: string;

  /**
   * used to determine if a prefix ('Week of' | 'Month of') should be added to the Date portion of hints
   */
  granularity?: string;
};
type LegendItem = {
  title: string;
  color: string;
  disabled: boolean;
};
/**
 * Generic Line Chart Component
 */

class LineChart extends React.Component<Props, State> {
  static displayName = 'LineChart';

  static defaultProps = DEFAULT_PROPS;

  // reference to the FlexibleXYPlot component
  plot: Record<string, any> | null | undefined;

  // reference to event handler that fires when LineChart is resized.
  subscribe: Record<string, any> | null | undefined;

  constructor(props: Props) {
    super(props);
    this.subscribe = null;
    this.state = {
      active: null,
      focus: null,
      plotWidth: 0,
      value: null,
      activeChartIndex: 0,
    };
  }

  componentDidMount() {
    // set initial plot width
    const plotWidth = R.path(['refs', 'container', 'clientWidth'], this.plot);
    this.setState({
      plotWidth,
    });
    // subscribe to changes in plot width
    this.subscribe = subscribe('resize', this.handleResize);
  }

  componentWillUnmount() {
    this.subscribe && this.subscribe.unsubscribe();
  }

  /**
   * Handle width responsive
   */
  handleResize = () => {
    const newWidth = R.path(['state', 'width'], this.plot);
    const currentWidth = this.state.plotWidth;

    if (newWidth !== currentWidth) {
      this.setState({
        plotWidth: newWidth,
      });
    }
  };

  /**
   * Render series
   * @param  {Object}  lines            lines data
   * @param  {Array}   sequence         sequence of showing data
   * @param  {Node}    Component        React component to be rendered
   * @param  {Boolean} showHint         render hint or not
   * @param  {Boolean} onlyShowOnActive render component only on active
   * param   {Integer} chartIndex       index of chart that series corresponds to
   * param   {String}  strokeStyle      style of series stroke (e.g. dashed vs. solid)
   * param   {String}  color            color of series
   * @param  {Object} [extraProps={}]   extra props to pass to component
   * @return {Node}                     Series
   */
  renderSeries = ({
    lines,
    sequence,
    Component,
    showHint,
    onlyShowOnActive,
    chartIndex,
    strokeStyle,
    color,
    extraProps = {},
  }: Record<string, any>) => {
    if (!lines || !sequence || !sequence.length || !Component) {
      return null;
    }

    const { colors = [] } = this.props;
    return sequence.map((key, i) => {
      const data = R.path([key, 'data'], lines);
      const { active, focus } = this.state;
      const isActive = active === key;
      const isFocus = focus === key;
      var { opacity } = extraProps;

      if (R.isNil(opacity)) {
        opacity = R.isNil(active) ? 1 : isActive ? 1 : 0.35;
      }

      return (
        <Component
          data={data}
          key={i}
          color={color || colors[(i + chartIndex) % colors.length]}
          strokeWidth={isFocus ? 2 : 1}
          size={isFocus ? 3 : 2}
          onSeriesMouseOut={() =>
            this.setState({
              focus: null,
            })
          }
          onSeriesMouseOver={() =>
            this.setState({
              focus: key,
            })
          }
          onValueClick={() => {
            if (this.state.active === key) {
              this.setState({
                active: null,
              });
            } else {
              this.setState({
                active: key,
              });
            }
          }}
          onValueMouseOver={
            showHint
              ? (value) => {
                  this.setState({
                    value: R.merge(
                      {
                        key,
                      },
                      value
                    ),
                    activeChartIndex: chartIndex,
                  });
                }
              : R.identity
          }
          onValueMouseOut={
            showHint
              ? (value) =>
                  this.setState({
                    value: null,
                  })
              : R.identity
          }
          {...extraProps}
          strokeStyle={strokeStyle}
          opacity={opacity}
          className={classNames(extraProps.className, 'h_pevent-a', {
            active: isActive,
            'h_d-n': onlyShowOnActive && !isActive,
          })}
        />
      );
    });
  };

  /**
   * Render hint component
   * @return {Node} React component for hint
   */
  renderHint = (yAxis: Record<string, any>) => {
    const { value } = this.state;
    const { xAxis, granularity } = this.props;

    if (!value) {
      return null;
    }

    const xTitle = xAxis.title || 'x';
    const yTitle = yAxis.title || 'y';
    const xFunc = xAxis.hintFormat || xAxis.tickFormat || R.identity;
    const yFunc = yAxis.hintFormat || yAxis.tickFormat || R.identity;
    // add prefix ('Week of' | 'Month of') to xValue if necessary
    let xValue = xFunc(value.x);

    if (granularity === 'week') {
      xValue = 'Week of ' + xValue;
    }

    if (granularity === 'month') {
      xValue = 'Month of ' + xValue;
    }

    const yValue = yFunc(value.y).toLocaleString(); // comma separate large numbers

    return (
      <Hint value={value}>
        <div className="rv-hint__content" data-testid={value.key}>
          <div>
            <span className="rv-hint__title h_fw-500">{xTitle}: </span>
            <span className="rv-hint__value">{xValue}</span>
          </div>
          <div>
            <span className="rv-hint__title h_fw-500">{yTitle}: </span>
            <span className="rv-hint__value">{yValue}</span>
          </div>
        </div>
      </Hint>
    );
  };

  /**
   * Render legend components
   * @param  {Array} items array of data
   * @return {Node}  React component for interactive legends
   */
  renderLegend = (items: Array<LegendItem>) => {
    if (!items || !items.length || !items[0]) {
      return;
    }

    return (
      <DiscreteColorLegend
        className="rv-legend"
        items={items}
        onItemClick={({ title }) => {
          if (title !== this.state.active) {
            this.setState({
              active: title,
            });
          } else {
            this.setState({
              active: null,
            });
          }
        }}
      />
    );
  };

  renderFlexibleXYPlot(
    lines: Record<string, any>,
    sequence: Array<string>,
    yAxis: Record<string, any>,
    hideXAxis: boolean,
    hideGridlines: boolean,
    chartIndex: number,
    className: string | null | undefined,
    strokeStyle: string
  ) {
    const {
      height,
      showHint,
      xAxis,
      xType,
      yType,
      includeDashedLine,
      includePointInDashedPart,
      includePointInSolidPart,
      ...otherProps
    } = this.props;
    // Responsive if tickValues passed in for xAxis
    let { tickValues } = xAxis;
    let xDomain;
    let yDomain;

    if (tickValues && xType) {
      // handle responsive
      var plotWidth = this.state.plotWidth;
      const scale = SCALE_FUNCTIONS[xType]().domain(tickValues).range([0, plotWidth]);
      const tickCounts = getTickCount(tickValues, plotWidth);

      // only scale the tick values if more than one tick
      if (tickCounts > 1) {
        tickValues = scale.ticks(tickCounts);

        if (xType === 'time') {
          tickValues = removeNonMidnightTicks(tickValues);
          tickValues = applyTimezoneOffsetToTickValues(tickValues);
          tickValues = addFirstOrLastTick(tickValues);
        }
      } else {
        if (xType === 'time') {
          xDomain = handleSingleDayDomain(tickValues);
          tickValues = handleSingleDayTick(tickValues);
          lines = handleSingleDayLines(lines);

          // only one point on the graph, center that point vertically
          if (sequence.length === 1) {
            yDomain = handleSinglePointDomain(lines, sequence[0]);
          }
        }
      }
    }

    // defaultProps only handle first layer from the props
    const propsXAxis = Object.assign({}, DEFAULT_PROPS.xAxis, xAxis, {
      tickValues,
    });
    const propsYAxis = Object.assign({}, DEFAULT_PROPS.yAxis, yAxis);
    const LINE_CONFIG = [
      // Area chart to show on active state, put on top to prevent covering line chart interaction
      {
        Component: AreaSeries,
        onlyShowOnActive: true,
        extraProps: {
          getNull: (d) => d.y !== null,
          opacity: 0.1,
          className: 'h_z-0 h_pos-r',
        },
      }, // Line + Mark
      {
        Component: LineMarkSeries,
        extraProps: {
          getNull: includePointInSolidPart,
        },
      }, // Dashed Line + Mark
      ...(includeDashedLine
        ? [
            {
              Component: LineMarkSeries,
              extraProps: {
                getNull: includePointInDashedPart,
                opacity: 0.35,
              },
            },
          ]
        : []), // Invisible layer to enlarge area for interaction
      {
        Component: LineMarkSeries,
        extraProps: {
          opacity: 0,
          strokeWidth: 10,
          className: 'h_z-0',
          size: 14,
        },
      },
    ];
    return (
      <div className={className} key={chartIndex}>
        <FlexibleXYPlot
          xType={xType}
          yType={yType}
          height={height}
          xDomain={tickValues.length === 1 ? xDomain : null}
          yDomain={sequence.length === 1 ? yDomain : null}
          className="chart-wrapper"
          {...otherProps}
          ref={(plot) => {
            if (plot) {
              this.plot = plot;
            }
          }}
        >
          {hideXAxis ? null : <XAxis {...propsXAxis} />}
          <YAxis {...propsYAxis} />
          {hideGridlines ? null : <HorizontalGridLines />}

          {LINE_CONFIG.map((config) =>
            this.renderSeries({
              lines,
              sequence,
              showHint,
              chartIndex,
              strokeStyle,
              ...config,
            })
          )}

          {showHint && this.state.activeChartIndex === chartIndex ? this.renderHint(yAxis) : null}
        </FlexibleXYPlot>
      </div>
    );
  }

  handleMultiAxes = () => {
    const { lines, colors = [] } = this.props;
    const sequence = lines.map((line) => Object.keys(line));
    const plot = lines.map((line, index) => {
      if (!line || R.isEmpty(line)) {
        return null;
      }

      let yAxis = !Array.isArray(this.props.yAxis) ? this.props.yAxis : this.props.yAxis[index];

      if (index > 0) {
        yAxis = R.assoc('orientation', 'right', yAxis);
        yAxis = R.assoc(
          'style',
          {
            line: {
              strokeDasharray: '6, 2',
            },
          },
          yAxis
        );
      }

      const className = classNames({
        // absolutely position the charts that are overlayed
        'multi-chart-wrapper': index > 0,
        // allow charts below to be clickable
        'h_pe-n': index > 0,
      });
      const strokeStyle = index > 0 ? 'dashed' : 'solid';
      return this.renderFlexibleXYPlot(line, sequence[index], yAxis, index > 0, index > 0, index, className, strokeStyle);
    });
    const legendItems = R.flatten(
      sequence.map((items, axisIndex) =>
        items.map((item, itemIndex) => ({
          title: item,
          color: axisIndex < 1 ? colors[itemIndex % colors.length] : colors[(itemIndex + 1) % colors.length],
          disabled: !!this.state.active && item !== this.state.active,
          strokeStyle: axisIndex < 1 ? 'solid' : 'dashed',
        }))
      )
    );
    return {
      plot,
      legendItems,
    };
  };

  handleSingleAxis = () => {
    const { lines, colors = [] } = this.props;
    const sequence = Object.keys(lines);
    const plot = this.renderFlexibleXYPlot(lines, sequence, this.props.yAxis, false, false, 0, null, 'solid');
    const legendItems = sequence.map((item, i) => ({
      title: item,
      color: colors[i % colors.length],
      disabled: !!this.state.active && item !== this.state.active,
    }));
    return {
      plot,
      legendItems,
    };
  };

  render() {
    const { showLegend, title, lines } = this.props;
    const { plot, legendItems } = Array.isArray(lines) ? this.handleMultiAxes() : this.handleSingleAxis();
    return (
      <div className="chart-wrapper-container">
        {title ? <div className="h_ta-c h_mb-5">{title}</div> : null}
        <div className="h_pos-r">
          {plot}
          {showLegend ? this.renderLegend(legendItems) : null}
        </div>
      </div>
    );
  }
}

export default LineChart;
